import { useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';

import {
  ApiResponse,
  ApiResponseError
} from 'types/services/baseServiceTypes';

/**
 * IUseService
 *
 * @typedef {IUseService}
 * @extends {ApiResponse}
 * @description Defines return model of the IUseService hook
 * @param {IGetOrganisationDetailsResponse | undefined} organisationDetailsResponse Get organisation details response
 */
export interface IUseService extends ApiResponse {
  loading: boolean;
}

/**
 * UseService hook
 *
 * @description Hook used to resolve axios services generically
 * @param {() => Promise<AxiosResponse<T>>} serviceFunction Axios function which needs to be resolved
 * @returns {IUseService & T} Returns a merged model of IUseService model and the selected Type T
 */
const useService = <T extends ApiResponse>(serviceFunction: () => Promise<AxiosResponse<T>>): IUseService & T => {
  const [response, setResponse] = useState<any | null>(null);
  const [wasSuccessful, setWasSuccessful] = useState<boolean>(false);
  const [errors, setErrors] = useState<Array<ApiResponseError>>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    resolveService();
  }, []);

  // Async method used to resolve the passed axios function
  const resolveService = async () => {
    setLoading(true);

    // Resolve the function
    try {
      const response = await serviceFunction();

      if (response.wasSuccessful) {
        setWasSuccessful(true);
        setLoading(false);
        setResponse(response);
      }

      if (response.errors != null) {
        setLoading(false);
        setErrors(response.errors);
      }
    }
    catch (error) {
      setLoading(false);
      setErrors([error as ApiResponseError]);
    }
  };

  return { loading, wasSuccessful, ...response, errors };
};

export default useService;
